import React, { useCallback, useEffect, useMemo, useState } from "react"
import { FiMaximize2, FiMinimize2 } from "react-icons/fi"

/**
 * Shared row-density control for every attendance table.
 *
 * "compact" is the default so more date/detail columns fit on screen without
 * horizontal scrolling; "comfortable" restores the original (larger) sizing.
 * The choice is kept in localStorage so it applies across all attendance
 * screens, not just the one where it was toggled.
 *
 * Only padding, type scale and column widths change — the columns, ordering
 * and cell content stay identical in both modes.
 */
export type TableDensity = "compact" | "comfortable"

const STORAGE_KEY = "attendance:table-density"
const DENSITY_EVENT = "attendance:table-density-change"

// Compact widths are a straight 75% of the original values so the sticky-column
// offsets stay proportional to what the comfortable layout already used.
const COMPACT_WIDTH_RATIO = 0.75

const readStoredDensity = (): TableDensity => {
  if (typeof window === "undefined") return "compact"
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "comfortable" ? "comfortable" : "compact"
  } catch {
    return "compact"
  }
}

export interface AttendanceDensity {
  density: TableDensity
  isCompact: boolean
  setDensity: (next: TableDensity) => void
  toggle: () => void
  /** Padding + type scale for a detail-column `<th>`. */
  headCell: string
  /** Padding + type scale for a detail-column `<td>`. */
  bodyCell: string
  /** Padding + type scale for a narrow matrix `<th>` (dates / status counts). */
  matrixHeadCell: string
  /** Padding + type scale for a matrix date `<td>`. */
  matrixDateCell: string
  /** Padding + type scale for a matrix status-count `<td>`. */
  matrixCountCell: string
  /** Vertical padding for the "loading" / "empty" placeholder rows. */
  placeholderCell: string
  /** Scales a comfortable-mode pixel width for the active density. */
  px: (comfortableWidth: number) => number
}

export const useAttendanceDensity = (): AttendanceDensity => {
  const [density, setDensityState] = useState<TableDensity>(readStoredDensity)

  // Keep every mounted attendance table in step when one of them toggles.
  useEffect(() => {
    const sync = () => setDensityState(readStoredDensity())
    window.addEventListener(DENSITY_EVENT, sync)
    window.addEventListener("storage", sync)
    return () => {
      window.removeEventListener(DENSITY_EVENT, sync)
      window.removeEventListener("storage", sync)
    }
  }, [])

  const setDensity = useCallback((next: TableDensity) => {
    setDensityState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* storage can be unavailable (private mode) — the in-memory value still applies */
    }
    window.dispatchEvent(new Event(DENSITY_EVENT))
  }, [])

  return useMemo(() => {
    const isCompact = density === "compact"

    return {
      density,
      isCompact,
      setDensity,
      toggle: () => setDensity(isCompact ? "comfortable" : "compact"),
      headCell: isCompact ? "px-2 py-1.5 text-[10px]" : "px-4 py-3 text-xs",
      bodyCell: isCompact ? "px-2 py-1.5 text-xs" : "px-4 py-3 text-sm",
      matrixHeadCell: isCompact ? "px-1 py-1.5 text-[10px]" : "px-2 py-3 text-xs",
      matrixDateCell: isCompact ? "px-1 py-1 text-[9px]" : "px-2 py-3 text-[10px]",
      matrixCountCell: isCompact ? "px-1 py-1 text-[10px]" : "px-2 py-3 text-xs",
      placeholderCell: isCompact ? "px-2 py-8" : "px-4 py-12",
      px: (comfortableWidth: number) =>
        isCompact ? Math.round(comfortableWidth * COMPACT_WIDTH_RATIO) : comfortableWidth,
    }
  }, [density, setDensity])
}

/** A frozen left-hand column: its width also drives the next column's offset. */
export interface StickyColumn {
  key: string
  label: string
  /** Width in the comfortable (original) layout. */
  width: number
}

export interface ResolvedStickyColumn extends StickyColumn {
  style: React.CSSProperties
}

/**
 * The frozen employee columns shared by the matrix and the members table.
 * Widths match the original `min-w-[…]` / `left-[…]` pairs at comfortable
 * density, so that layout is unchanged when the user expands.
 */
export const ATTENDANCE_STICKY_COLUMNS: StickyColumn[] = [
  { key: "employee_code", label: "E-Code", width: 80 },
  { key: "username", label: "Username", width: 100 },
  { key: "user_id", label: "Userid", width: 80 },
  { key: "designation", label: "Designation", width: 120 },
  { key: "role", label: "Role", width: 80 },
  { key: "association", label: "Association", width: 120 },
  { key: "reporting_person", label: "Reporting Person", width: 140 },
]

/**
 * Turns sticky column widths into cumulative `left` offsets. Inline styles are
 * used rather than Tailwind `left-[…]` classes because the offsets are computed
 * at runtime from the active density.
 */
export const buildStickyLayout = (
  columns: StickyColumn[],
  px: (comfortableWidth: number) => number
): ResolvedStickyColumn[] => {
  let offset = 0
  return columns.map((column) => {
    const width = px(column.width)
    const resolved: ResolvedStickyColumn = {
      ...column,
      style: { left: offset, minWidth: width },
    }
    offset += width
    return resolved
  })
}

interface DensityToggleProps {
  density: AttendanceDensity
  className?: string
}

/**
 * Single button that swaps between the compact table and the original
 * (expanded) one. The label always names the action it performs.
 */
export const DensityToggle: React.FC<DensityToggleProps> = ({ density, className = "" }) => (
  <button
    type="button"
    onClick={density.toggle}
    title={
      density.isCompact
        ? "Expand rows back to the full size"
        : "Shrink rows so more columns fit on screen"
    }
    className={`px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-2 ${className}`}
  >
    {density.isCompact ? <FiMaximize2 className="w-4 h-4" /> : <FiMinimize2 className="w-4 h-4" />}
    {density.isCompact ? "Expand" : "Compact"}
  </button>
)
